import { Box, Button, Stack, Typography } from "@mui/material";

import { styled } from "@mui/material/styles";

export const ModalTitleContainer = styled(Box)(({ theme }) => ({
    boxShadow: `0px 10px 20px -10px ${theme.colors.action.hoverShadow}`,
    padding: "24px 40px",
}));

export const ConfirmationButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

export const StatsRow = styled(Box)(() => ({
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    padding: "40px",
    gap: "40px",
    borderBottom: `1px solid #C0C1C3`,
    width: "100%",
}));

export const StatBox = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    width: "160px",
}));

interface ColumnProps {
    isSelected: boolean;
}

interface AttendeeBoxProps {
    isSelected: boolean;
    isLast: boolean;
}

export const AttendeeBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isLast" && prop !== "isSelected",
})<AttendeeBoxProps>(({ theme, isLast, isSelected }) => ({
    position: "relative",
    height: "72px",
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 2),
    boxSizing: "border-box",
    borderRadius: isSelected ? theme.spacing(1) : 0,
    zIndex: isSelected ? 1 : 0,

    backgroundColor: isSelected
        ? `${theme.colors.secondary.purple}1A`
        : "transparent",

    border: isSelected
        ? `1px solid ${theme.colors.secondary.purple}`
        : "1px solid transparent",

    borderBottom:
        !isSelected && !isLast
            ? `1px solid ${theme.colors.neutral.softGray}`
            : isSelected
                ? `1px solid ${theme.colors.secondary.purple}`
                : "none",

    marginBottom: isSelected ? "-1px" : 0,
}));

export const LeftColumn = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isSelected",
})<ColumnProps>(({ theme, isSelected }) => ({
    width: "100%",
    padding: theme.spacing(2),
    display: "block",
    [theme.breakpoints.up("tablet")]: {
        width: "50%",
        borderRight: `1px solid ${theme.colors.action.modalClose}`,
    },
    [theme.breakpoints.up("desktop")]: {
        width: "30%",
    },
    [theme.breakpoints.down("tablet")]: {
        display: isSelected ? "none" : "block",
    },
}));

export const RightColumn = styled(Box, {
    shouldForwardProp: (prop) => prop !== "isSelected",
})<ColumnProps>(({ theme, isSelected }) => ({
    width: "100%",
    padding: theme.spacing(2),
    position: "relative",
    display: "none",
    [theme.breakpoints.up("tablet")]: {
        width: "50%",
        display: "block",
    },
    [theme.breakpoints.up("desktop")]: {
        width: "70%",
        display: "block",
    },
    [theme.breakpoints.down("tablet")]: {
        display: isSelected ? "block" : "none",
    },
}));

export const InnerLeftColumn = styled(Box)(({ theme }) => ({
    height: "100%",
    overflowY: "auto",
    overflowX: "visible",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(6),
    position: "relative",
    "&::-webkit-scrollbar-track": {
        backgroundColor: theme.colors.neutral.white,
        borderRadius: "6px",
        marginBottom: "12px",
        padding: "2px",
    },
    "&::-webkit-scrollbar": {
        width: "6px",
        WebkitAppearance: "none",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.colors.primary.slalomBlue,
        borderRadius: "6px",
        margin: "2px",
    },
}));

export const InnerRightColumn = styled(Box)(({ theme }) => ({
    height: "100%",
    overflowY: "auto",
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(6),
    "&::-webkit-scrollbar-track": {
        backgroundColor: theme.colors.neutral.white,
        borderRadius: "6px",
        marginBottom: "12px",
        padding: "2px",
    },
    "&::-webkit-scrollbar": {
        width: "10px",
        WebkitAppearance: "none",
    },
    "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.colors.primary.slalomBlue,
        borderRadius: "6px",
        margin: "2px",
    },
}));

export const HorizontalStack = styled(Stack)(({ theme }) => ({
    height: "100%",
    padding: theme.spacing(1),
    [theme.breakpoints.down("tablet")]: {
        padding: `${theme.spacing(2)} ${theme.spacing(0)}`,
    },
}));

export const SurveyAnswerContainer = styled(Box)(({ theme }) => ({
    backgroundColor: `${theme.colors.secondary.purple}1A`,
    borderRadius: theme.spacing(1),
    margin: theme.spacing(1),
    padding: `${theme.spacing(3)} ${theme.spacing(2)}`,
}));

export const SubtitleActionText = styled(Typography)(({ theme }) => ({
    cursor: "pointer",
    transition: "opacity 0.3s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    "&:hover": {
        opacity: 0.7,
    },
    color: theme.colors.primary.slalomDarkBlue,
    textAlign: "right",
    marginTop: theme.spacing(2),
}));

export const SubtitleActionTextLoading = styled(Typography)(({ theme }) => ({
    color: theme.colors.neutral.gray,
    textAlign: "right",
    marginTop: theme.spacing(2),
}));
